import React from 'react';
import SearchBar from './SearchBar';
// Import icons CSS directly from the component's assets folder
import '../assets/icons/icons.css';
import { inclination3D, rotation3D } from '../lib/defaults.ts';

type ViewType = 'FLOW' | 'TABULAR';
type RenderMode = 'FLOW_THROUGH' | 'NO_FLOW';
type ViewDimension = '2D' | '3D';

interface ToolbarProps {
  onSearch: (searchTerm: string) => void;
  viewType: ViewType;
  onViewTypeChange: (viewType: ViewType) => void;
  onOpenFile?: () => void;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onFitGraph?: () => void;
  onExportSvg?: () => void;
  onExportPng?: () => void;
  onExportPdf?: () => void;
  onPrint?: () => void;
  onExpandAll?: () => void;
  onCollapseAll?: () => void;
  onResetFilter?: () => void;
  onRenderModeChange?: (mode: RenderMode) => void;
  renderMode?: RenderMode;
  onViewDimensionChange?: (dimension: ViewDimension) => void;
  viewDimension?: ViewDimension;
  orientation?: {rotation: number, inclination: number};
  setOrientation: (orientation: {rotation: number, inclination: number}) => void;
}

const Toolbar: React.FC<ToolbarProps> = ({
  onSearch,
  viewType,
  onViewTypeChange,
  onZoomIn,
  onZoomOut,
  onFitGraph,
  onExportSvg,
  onExportPng,
  onExportPdf,
  onExpandAll,
  onCollapseAll,
  onResetFilter,
  onViewDimensionChange,
  viewDimension = '2D',
  orientation = {rotation: rotation3D, inclination: inclination3D},
  setOrientation
}) => {
  const toolbarStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'white',
    paddingRight: '5px',
    height: '48px',
    paddingLeft: '5px',
    color: '#151E47',
    fontFamily: 'Arial, sans-serif',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    position: 'absolute',
    bottom: '10px',
    right: '0',
    zIndex: 49
  };

  const actionsContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    paddingRight: '10px',
    gap: '5px'
  };

  const baseButtonStyle: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    color: '#151E47',
    cursor: 'pointer',
    padding: '6px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: 'none',
    transition: 'background-color 0.2s ease'
  };

  const iconStyle: React.CSSProperties = {
    width: '20px',
    height: '20px',
    color: '#151E47'
  };

  const textButtonStyle: React.CSSProperties = {
    ...baseButtonStyle,
    fontSize: '14px'
  };

  const separatorStyle: React.CSSProperties = {
    width: '1px',
    height: '25px',
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    margin: '0 8px'
  };

  const getActiveStyle = (isActive: boolean): React.CSSProperties => ({
    backgroundColor: isActive ? 'rgba(0, 0, 0, 0.1)' : 'transparent'
  });

  return (
    <div style={toolbarStyle}>
      <div style={actionsContainerStyle}>
        {/* Zoom controls */}
        <button style={baseButtonStyle} title="Increase Zoom" onClick={onZoomIn}>
          <i className="demo-icon demo-icon-yIconZoomIn" style={iconStyle} />
        </button>
        <button style={baseButtonStyle} title="Decrease Zoom" onClick={onZoomOut}>
          <i className="demo-icon demo-icon-yIconZoomOut" style={iconStyle} />
        </button>
        <button style={baseButtonStyle} title="Fit Graph Bounds" onClick={onFitGraph}>
          <i className="demo-icon demo-icon-yIconZoomFit" style={iconStyle} />
        </button>

        <span style={separatorStyle} />

        {/* Export options */}
        <button title="Export as SVG" onClick={onExportSvg} style={textButtonStyle}>
          SVG
        </button>
        <button title="Export as PNG" onClick={onExportPng} style={textButtonStyle}>
          PNG
        </button>
        <button title="Export as PDF" onClick={onExportPdf} style={textButtonStyle}>
          PDF
        </button>

        <span style={separatorStyle} />

        {/* Expand/Collapse */}
        <button style={baseButtonStyle} title="Expand all" onClick={onExpandAll}>
          <i className="demo-icon demo-icon-expand-all" style={iconStyle} />
        </button>
        <button style={baseButtonStyle} title="Collapse all" onClick={onCollapseAll}>
          <i className="demo-icon demo-icon-collapse-all" style={iconStyle} />
        </button>

        <span style={separatorStyle} />

        {/* View type toggle - Replaced with buttons for styling consistency */}
        <button
          title="Flow View"
          onClick={() => onViewTypeChange('FLOW')}
          style={{ ...textButtonStyle, ...getActiveStyle(viewType === 'FLOW') }}
        >
          Flow
        </button>
        <button
          title="Tabular View"
          onClick={() => onViewTypeChange('TABULAR')}
          style={{ ...textButtonStyle, ...getActiveStyle(viewType === 'TABULAR') }}
        >
          Tabular
        </button>

        <span style={separatorStyle} />

        {/* Filter reset */}
        <button style={baseButtonStyle} title="Reset filter" onClick={onResetFilter}>
          <i className="demo-icon demo-icon-filter-off" style={iconStyle} />
        </button>

        <span style={separatorStyle} />

        {/* 2D/3D view */}
        <button
          style={{ ...baseButtonStyle, ...getActiveStyle(viewDimension === '2D') }}
          title="Show flat view (2D)"
          onClick={() => onViewDimensionChange?.('2D')}
        >
          <i className="demo-icon demo-icon-2d" style={iconStyle} />
        </button>
        <button
          style={{ ...baseButtonStyle, ...getActiveStyle(viewDimension === '3D') }}
          title="Show isometric view (3D)"
          onClick={() => onViewDimensionChange?.('3D')}
        >
          <i className="demo-icon demo-icon-3d" style={iconStyle} />
        </button>
      </div>

      {/* Right side controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
        {/* Rotation slider */}
        <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          Rotation:
          <input
            type="range"
            min={-175}
            max={175}
            value={orientation.rotation}
            step="1"
            onChange={(e) => { const r = parseInt(e.target.value); setOrientation({...orientation, rotation: r === 0 ? 1 : r})}}
          />
        </label>

        {/* Inclination slider */}
        <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          Inclination:
          <input
            type="range"
            min={1}
            max={90}
            value={orientation.inclination}
            step="1"
            onChange={(e) => { setOrientation({...orientation, inclination: parseInt(e.target.value)})}}
          />
        </label>

        {/* Search Bar */}
        <SearchBar onSearch={onSearch} />
      </div>
    </div>
  );
};

export default Toolbar;
